import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import DatePicker from '../../components/ui/DatePicker';
import LoadingState from '../../components/ui/LoadingState';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../context/AuthContext';
import {
  fetchCurrentEmployee,
  updateCurrentEmployeeProfile,
} from '../../lib/api/employees';
import { updateUserSettings } from '../../lib/api/userSettings';
import useThemePreference from '../../hooks/useThemePreference';
import useTranslation from '../../hooks/useTranslation';
import { cn } from '../../utils/cn';

const SettingsPage = () => {
  const { refreshProfile } = useAuth();
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [loadingError, setLoadingError] = useState(null);
  const [profile, setProfile] = useState(null);
  const [formValues, setFormValues] = useState({
    fullName: '',
    email: '',
    phone: '',
    address: '',
    shiftPreference: '',
    birthday: '',
    gender: '',
  });
  const [savingPersonal, setSavingPersonal] = useState(false);
  const [personalStatus, setPersonalStatus] = useState(null);
  const [languageStatus, setLanguageStatus] = useState(null);
  const [appearanceStatus, setAppearanceStatus] = useState(null);
  const [savingAppearance, setSavingAppearance] = useState(false);
  const [savingLanguage, setSavingLanguage] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const fileInputRef = useRef(null);

  const avatarUrl =
    profile?.avatar
    ?? profile?.avatarUrl
    ?? profile?.raw?.avatarUrl
    ?? null;

  const {
    preference: themePreference,
    setPreference: setThemePreference,
  } = useThemePreference();
  const {
    t,
    language,
    setLanguage,
    availableLanguages,
  } = useTranslation(['settings', 'common']);

  const initialValues = useMemo(
    () => ({
      fullName: profile?.fullName ?? profile?.raw?.fullName ?? '',
      email: profile?.email ?? '',
      phone: profile?.phone ?? '',
      address: profile?.address ?? '',
      shiftPreference: profile?.raw?.shiftPreference ?? '',
      birthday: profile?.birthday ?? '',
      gender: profile?.gender ?? profile?.raw?.gender ?? '',
    }),
    [profile],
  );

  const loadProfile = useCallback(async () => {
    setLoadingProfile(true);
    setLoadingError(null);
    try {
      const data = await fetchCurrentEmployee();
      if (!data) {
        setProfile(null);
        setLoadingError(t('error.missingProfile'));
      } else {
        setProfile(data);
      }
    } catch (error) {
      setProfile(null);
      setLoadingError(error?.response?.data?.message ?? t('error.body'));
    } finally {
      setLoadingProfile(false);
    }
  }, [t]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    setFormValues(initialValues);
  }, [initialValues]);

  const normalizedInitial = useMemo(
    () => ({
      fullName: (initialValues.fullName ?? '').trim(),
      email: (initialValues.email ?? '').trim(),
      phone: (initialValues.phone ?? '').trim(),
      address: (initialValues.address ?? '').trim(),
      shiftPreference: (initialValues.shiftPreference ?? '').trim(),
      birthday: initialValues.birthday ?? '',
      gender: (initialValues.gender ?? '').trim(),
    }),
    [initialValues],
  );

  const formHasChanges = useMemo(() => {
    if (!profile) {
      return false;
    }

    const trimmed = {
      fullName: (formValues.fullName ?? '').trim(),
      email: (formValues.email ?? '').trim(),
      phone: (formValues.phone ?? '').trim(),
      address: (formValues.address ?? '').trim(),
      shiftPreference: (formValues.shiftPreference ?? '').trim(),
      birthday: formValues.birthday ?? '',
      gender: (formValues.gender ?? '').trim(),
    };

    if (trimmed.birthday !== normalizedInitial.birthday) {
      return true;
    }

    return ['fullName', 'email', 'phone', 'address', 'shiftPreference', 'gender'].some(
      (field) => trimmed[field] !== normalizedInitial[field],
    );
  }, [formValues, normalizedInitial, profile]);

  const displayInitial = useMemo(() => {
    const candidates = [
      (formValues.fullName ?? '').trim(),
      profile?.fullName ?? '',
      profile?.name ?? '',
      profile?.email ?? '',
    ];

    for (const value of candidates) {
      if (typeof value !== 'string') {
        continue;
      }
      const trimmed = value.trim();
      if (trimmed) {
        return trimmed.charAt(0).toUpperCase();
      }
    }
    return 'U';
  }, [formValues.fullName, profile]);

  const extractErrorMessage = useCallback((error, fallback) => (
    error?.response?.data?.message
      ?? error?.message
      ?? fallback
  ), []);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormValues((previous) => ({
      ...previous,
      [name]: value,
    }));
    setPersonalStatus(null);
  };

  const handleGenderChange = (value) => {
    setFormValues((previous) => ({
      ...previous,
      gender: typeof value === 'string' ? value : '',
    }));
    setPersonalStatus(null);
  };

  const handlePersonalSubmit = async (event) => {
    event.preventDefault();
    if (!profile) {
      return;
    }

    if (!formHasChanges) {
      setPersonalStatus({
        type: 'success',
        key: 'personal.status.noChanges',
      });
      return;
    }

    setSavingPersonal(true);
    setPersonalStatus(null);

    const payload = {};
    const trimmedName = (formValues.fullName ?? '').trim();
    const trimmedEmail = (formValues.email ?? '').trim();
    const trimmedPhone = (formValues.phone ?? '').trim();
    const trimmedAddress = (formValues.address ?? '').trim();
    const trimmedShift = (formValues.shiftPreference ?? '').trim();
    const trimmedGender = (formValues.gender ?? '').trim();

    if (trimmedName !== normalizedInitial.fullName) {
      payload.fullName = trimmedName;
    }
    if (trimmedEmail !== normalizedInitial.email) {
      payload.email = trimmedEmail;
    }
    if (trimmedPhone !== normalizedInitial.phone) {
      payload.phone = trimmedPhone;
    }
    if (trimmedAddress !== normalizedInitial.address) {
      payload.address = trimmedAddress;
    }
    if (trimmedShift !== normalizedInitial.shiftPreference) {
      payload.shiftPreference = trimmedShift;
    }
    if (trimmedGender !== normalizedInitial.gender) {
      if (trimmedGender === 'male' || trimmedGender === 'female') {
        payload.gender = trimmedGender;
      }
    }
    if ((formValues.birthday ?? '') !== normalizedInitial.birthday) {
      if (formValues.birthday) {
        const date = new Date(`${formValues.birthday}T00:00:00.000Z`);
        if (!Number.isNaN(date.getTime())) {
          payload.birthday = date.toISOString();
        }
      }
    }

    if (!Object.keys(payload).length) {
      setSavingPersonal(false);
      setPersonalStatus({
        type: 'success',
        key: 'personal.status.noChanges',
      });
      return;
    }

    try {
      const updatedProfile = await updateCurrentEmployeeProfile(payload);
      if (!updatedProfile) {
        throw new Error(t('personal.errors.updateMissing'));
      }

      setProfile(updatedProfile);
      setPersonalStatus({
        type: 'success',
        key: 'personal.status.updated',
      });
      await refreshProfile();
    } catch (error) {
      setPersonalStatus({
        type: 'error',
        message: extractErrorMessage(error, t('personal.errors.save')),
      });
    } finally {
      setSavingPersonal(false);
    }
  };

  const handleAvatarUpload = async (event) => {
    const input = event.target;
    const file = input?.files?.[0] ?? null;
    if (input) {
      input.value = '';
    }
    if (!file) {
      return;
    }

    if (!file.type?.startsWith('image/')) {
      setPersonalStatus({
        type: 'error',
        key: 'personal.errors.imageType',
      });
      return;
    }

    const MAX_FILE_SIZE = 5 * 1024 * 1024;
    if (file.size > MAX_FILE_SIZE) {
      setPersonalStatus({
        type: 'error',
        key: 'personal.errors.imageSize',
      });
      return;
    }

    const readFileAsDataUrl = () =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          if (typeof reader.result === 'string') {
            resolve(reader.result);
          } else {
            reject(new Error(t('personal.errors.imageUnsupported')));
          }
        };
        reader.onerror = () => reject(new Error(t('personal.errors.imageRead')));
        reader.readAsDataURL(file);
      });

    setUploadingAvatar(true);
    setPersonalStatus(null);

    try {
      const dataUrl = await readFileAsDataUrl();
      const updatedProfile = await updateCurrentEmployeeProfile({
        avatarImageBase64: dataUrl,
        avatarImageType: file.type,
        avatarImageName: file.name,
      });
      if (!updatedProfile) {
        throw new Error(t('personal.errors.updateMissing'));
      }

      setProfile(updatedProfile);
      setPersonalStatus({
        type: 'success',
        key: 'personal.status.photoUpdated',
      });
      await refreshProfile();
    } catch (error) {
      setPersonalStatus({
        type: 'error',
        message: extractErrorMessage(error, t('personal.errors.photoUpdate')),
      });
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleAvatarRemoval = async () => {
    if (!profile?.avatarFileId) {
      return;
    }

    setUploadingAvatar(true);
    setPersonalStatus(null);

    try {
      const updatedProfile = await updateCurrentEmployeeProfile({
        avatarFileId: null,
      });
      if (!updatedProfile) {
        throw new Error(t('personal.errors.updateMissing'));
      }

      setProfile(updatedProfile);
      setPersonalStatus({
        type: 'success',
        key: 'personal.status.photoRemoved',
      });
      await refreshProfile();
    } catch (error) {
      setPersonalStatus({
        type: 'error',
        message: extractErrorMessage(error, t('personal.errors.photoRemove')),
      });
    } finally {
      setUploadingAvatar(false);
    }
  };

  const genderOptions = useMemo(
    () => [
      { value: 'male', label: t('personal.form.gender.options.male', 'Male') },
      { value: 'female', label: t('personal.form.gender.options.female', 'Female') },
    ],
    [t],
  );

  const themeOptions = useMemo(
    () => [
      {
        value: 'light',
        label: t('appearance.options.light.title'),
        description: t('appearance.options.light.description'),
        icon: 'Sunrise',
      },
      {
        value: 'dark',
        label: t('appearance.options.dark.title'),
        description: t('appearance.options.dark.description'),
        icon: 'Moon',
      },
      {
        value: 'system',
        label: t('appearance.options.system.title'),
        description: t('appearance.options.system.description'),
        icon: 'Monitor',
      },
    ],
    [t],
  );

  const languageOptions = useMemo(
    () =>
      availableLanguages.map((code) => ({
        value: code,
        label: t(`languageNames.${code}`),
      })),
    [availableLanguages, t],
  );

  const handleLanguageChange = useCallback(
    async (nextLanguage) => {
      if (!nextLanguage) {
        return;
      }

      const normalized = typeof nextLanguage === 'string' ? nextLanguage.trim() : '';
      if (!normalized) {
        return;
      }

      if (normalized === language) {
        setLanguageStatus({
          type: 'success',
          key: 'language.status.updated',
        });
        return;
      }

      const previousLanguage = language;

      setSavingLanguage(true);
      setLanguageStatus(null);
      setLanguage(normalized);

      try {
        await updateUserSettings({ language: normalized });
        setLanguageStatus({
          type: 'success',
          key: 'language.status.updated',
        });
        await refreshProfile();
      } catch (error) {
        setLanguage(previousLanguage);
        setLanguageStatus({
          type: 'error',
          message: extractErrorMessage(error, t('language.errors.update')),
        });
      } finally {
        setSavingLanguage(false);
      }
    },
    [extractErrorMessage, language, refreshProfile, setLanguage, t],
  );

  const handleThemeChange = useCallback(
    async (nextPreference) => {
      if (!nextPreference) {
        return;
      }

      const previousPreference = themePreference;
      if (nextPreference === themePreference) {
        setAppearanceStatus({
          type: 'success',
          key: 'appearance.status.updated',
        });
        return;
      }

      setSavingAppearance(true);
      setAppearanceStatus(null);
      setThemePreference(nextPreference);

      try {
        await updateUserSettings({ themePreference: nextPreference });
        setAppearanceStatus({
          type: 'success',
          key: 'appearance.status.updated',
        });
        await refreshProfile();
      } catch (error) {
        setThemePreference(previousPreference);
        setAppearanceStatus({
          type: 'error',
          message: extractErrorMessage(error, t('appearance.errors.update')),
        });
      } finally {
        setSavingAppearance(false);
      }
    },
    [extractErrorMessage, refreshProfile, t, themePreference, setThemePreference],
  );

  return (
    <div className="min-h-screen bg-background">
      <Header allowLocationSelection={false} />
      <main className="pt-24 pb-12">
        <div className="mx-auto w-full max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground">{t('title')}</h1>
            <p className="mt-2 text-base text-muted-foreground">
              {t('description')}
            </p>
          </div>

          {loadingProfile ? (
            <LoadingState message={t('loading')} heightClassName="h-64" />
          ) : (
            <div className="space-y-8">
              {loadingError && (
                <div className="rounded-lg border border-warning/30 bg-warning/10 px-4 py-4 text-sm text-warning-foreground">
                  <div className="flex items-start gap-3">
                    <Icon name="AlertTriangle" size={20} className="mt-0.5 text-warning-foreground" />
                    <div>
                      <p className="font-medium">{t('error.headline')}</p>
                      <p className="mt-1 leading-relaxed">{loadingError}</p>
                      <div className="mt-3">
                        <Button variant="outline" size="sm" onClick={() => loadProfile()}>
                          {t('actions.tryAgain')}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <section className="rounded-2xl border border-border bg-card shadow-elevation-1">
                <div className="border-b border-border px-6 py-5">
                  <h2 className="text-xl font-semibold text-foreground">{t('personal.heading')}</h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {t('personal.description')}
                  </p>
                </div>

                <div className="px-6 py-6">
                  {!profile ? (
                    <div className="rounded-lg border border-muted bg-muted/40 px-4 py-6 text-sm text-muted-foreground">
                      {t('error.missingProfile')}
                    </div>
                  ) : (
                    <form className="space-y-6" onSubmit={handlePersonalSubmit}>
                      {personalStatus && (
                        <div
                          className={cn(
                            'rounded-lg border px-4 py-3 text-sm',
                            personalStatus.type === 'success'
                              ? 'border-success/30 bg-success/20 text-success-foreground'
                              : 'border-error/30 bg-error/10 text-error-foreground',
                          )}
                        >
                          {personalStatus.key
                            ? t(personalStatus.key)
                            : personalStatus.message}
                        </div>
                      )}

                      <div className="flex flex-col gap-6 lg:flex-row">
                          <div className="flex items-center gap-4">
                            <div className="relative h-20 w-20 overflow-hidden rounded-full border border-border bg-muted text-lg font-semibold text-foreground">
                              {avatarUrl ? (
                                <img
                                  src={avatarUrl}
                                alt={t('personal.avatar.label')}
                                  className="h-full w-full object-cover"
                                />
                              ) : (
                                <div className="flex h-full w-full items-center justify-center bg-muted text-2xl">
                                  {loadingProfile ? (
                                    <span
                                      className="h-6 w-6 animate-spin rounded-full border-2 border-muted-foreground/40 border-t-primary"
                                      aria-label={t('status.loadingAvatar')}
                                    />
                                  ) : (
                                    displayInitial
                                  )}
                              </div>
                            )}
                            {uploadingAvatar && (
                              <div className="absolute inset-0 flex items-center justify-center bg-background/70">
                                <span className="h-6 w-6 animate-spin rounded-full border-2 border-primary/20 border-t-primary" aria-hidden="true" />
                              </div>
                            )}
                          </div>
                          <div className="space-y-2">
                            <div>
                              <p className="text-sm font-medium text-foreground">{t('personal.avatar.label')}</p>
                              <p className="text-xs text-muted-foreground">
                                {t('personal.avatar.helper')}
                              </p>
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                              <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/png,image/jpeg"
                                className="hidden"
                                onChange={handleAvatarUpload}
                              />
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploadingAvatar}
                                loading={uploadingAvatar}
                              >
                                {t('actions.uploadNewPhoto')}
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={handleAvatarRemoval}
                                disabled={!profile?.avatarFileId || uploadingAvatar}
                              >
                                {t('actions.remove')}
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="grid gap-6 md:grid-cols-2">
                        <Input
                          name="fullName"
                          label={t('personal.form.fullName.label')}
                          placeholder={t('personal.form.fullName.placeholder')}
                          value={formValues.fullName}
                          onChange={handleInputChange}
                        />
                        <Input
                          name="email"
                          type="email"
                          label={t('personal.form.email.label')}
                          placeholder={t('personal.form.email.placeholder')}
                          value={formValues.email}
                          onChange={handleInputChange}
                        />
                        <Input
                          name="phone"
                          label={t('personal.form.phone.label')}
                          placeholder={t('personal.form.phone.placeholder')}
                          value={formValues.phone}
                          onChange={handleInputChange}
                        />
                        <DatePicker
                          name="birthday"
                          label={t('personal.form.birthday.label')}
                          value={formValues.birthday}
                          onChange={handleInputChange}
                        />
                        <Input
                          name="address"
                          label={t('personal.form.address.label')}
                          placeholder={t('personal.form.address.placeholder')}
                          value={formValues.address}
                          onChange={handleInputChange}
                        />
                        <Input
                          name="shiftPreference"
                          label={t('personal.form.shiftPreference.label')}
                          placeholder={t('personal.form.shiftPreference.placeholder')}
                      value={formValues.shiftPreference}
                      onChange={handleInputChange}
                    />
                    <Select
                      name="gender"
                      label={t('personal.form.gender.label')}
                      placeholder={t('personal.form.gender.placeholder')}
                      options={genderOptions}
                      value={formValues.gender}
                      onChange={handleGenderChange}
                    />
                  </div>

                  <div className="flex justify-end">
                        <Button type="submit" disabled={savingPersonal || uploadingAvatar || !formHasChanges} loading={savingPersonal}>
                          {t('actions.saveChanges')}
                        </Button>
                      </div>
                    </form>
                  )}
                </div>
              </section>

              <section className="rounded-2xl border border-border bg-card shadow-elevation-1">
                <div className="border-b border-border px-6 py-5">
                  <h2 className="text-xl font-semibold text-foreground">{t('appearance.heading')}</h2>
                </div>

                <div className="space-y-6 px-6 py-6">
                  {appearanceStatus && (
                    <div
                      className={cn(
                        'rounded-lg border px-4 py-3 text-sm',
                          appearanceStatus.type === 'success'
                            ? 'border-success/30 bg-success/20 text-success-foreground'
                          : 'border-error/30 bg-error/10 text-error-foreground',
                      )}
                    >
                      {appearanceStatus.key
                        ? t(appearanceStatus.key)
                        : appearanceStatus.message}
                    </div>
                  )}

                  <div className="grid gap-4 md:grid-cols-3">
                    {themeOptions.map((option) => {
                      const isSelected = themePreference === option.value;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => handleThemeChange(option.value)}
                          disabled={savingAppearance}
                          className={cn(
                            'group flex h-full w-full flex-col gap-3 rounded-xl border px-4 py-4 text-left transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                            isSelected
                              ? 'border-primary bg-primary/10'
                              : 'border-border hover:border-primary/40 hover:bg-muted/40',
                          )}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-primary">
                              <Icon name={option.icon} size={20} />
                            </div>
                            {isSelected && (
                              <Icon name="Check" size={18} className="text-primary" />
                            )}
                          </div>
                          <div>
                            <p className="text-base font-semibold text-foreground">{option.label}</p>
                            <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{option.description}</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                </div>
              </section>

              <section className="rounded-2xl border border-border bg-card shadow-elevation-1">
                <div className="border-b border-border px-6 py-5">
                  <h2 className="text-xl font-semibold text-foreground">{t('language.heading')}</h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {t('language.description')}
                  </p>
                </div>

                <div className="space-y-4 px-6 py-6">
                  {languageStatus && (
                    <div
                      className={cn(
                        'rounded-lg border px-4 py-3 text-sm',
                          languageStatus.type === 'success'
                            ? 'border-success/30 bg-success/10 text-success'
                          : 'border-error/30 bg-error/10 text-error-foreground',
                      )}
                    >
                      {languageStatus.key
                        ? t(languageStatus.key)
                        : languageStatus.message}
                    </div>
                  )}

                  <Select
                    options={languageOptions}
                    value={language}
                    onChange={handleLanguageChange}
                    label={t('language.heading')}
                    placeholder={t('language.placeholder')}
                    disabled={languageOptions.length === 0 || savingLanguage}
                    loading={savingLanguage}
                  />

                  <p className="text-sm text-muted-foreground">{t('language.helper')}</p>
                </div>
              </section>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default SettingsPage;
